/**
 * Trip duration and price buckets — single source of truth for the
 * `/zajezdy` filter sidebar (duration), the trip-planning quiz (duration +
 * budget) and the homepage trip-type tiles (virtual `day`/`multi` keys are
 * translated by the listing page, not defined here).
 *
 * When the admin wants different bucket boundaries or labels, edit here
 * and they propagate everywhere automatically.
 */

export interface TripBucket {
  /** URL-safe identifier used for `?duration=` query and React keys. */
  key: string;
  /** User-facing label shown in filters and the quiz (Czech, web-first). */
  label: string;
  /** Lower bound, inclusive. */
  min: number;
  /** Upper bound, inclusive. `null` means open-ended (infinity). */
  max: number | null;
  /** Emoji shown next to the label in the quiz UI. Optional. */
  icon?: string;
}

/** Trip duration in days (řez 05 — design buckets with unambiguous bounds).
 * Ranges are contiguous — the full `[0, ∞)` is covered with no holes; day
 * trips (0–1 day) fall into "1-10". `as const` keeps `key` literals narrow so
 * consumers (zod enums, lookup-by-key) get compile-time safety. */
export const DURATION_BUCKETS = [
  { key: "1-10", label: "1–10 dní", min: 0, max: 10, icon: "📅" },
  { key: "11-14", label: "11–14 dní", min: 11, max: 14, icon: "🗓️" },
  { key: "15-21", label: "15–21 dní", min: 15, max: 21, icon: "🧳" },
  { key: "22-plus", label: "22 a více dní", min: 22, max: null, icon: "🌍" },
] as const satisfies readonly TripBucket[];

export type DurationBucketKey = (typeof DURATION_BUCKETS)[number]["key"];

/** Trip price per person in CZK — quiz budget question only; the listing
 * filter is the Kč slider (`PRICE_SLIDER` → priceMin/priceMax params). */
export const PRICE_BUCKETS = [
  { key: "lt40k", label: "Do 40 000 Kč", min: 0, max: 39999, icon: "💰" },
  { key: "40k_80k", label: "40 000 – 80 000 Kč", min: 40000, max: 79999, icon: "💰💰" },
  { key: "80k_150k", label: "80 000 – 150 000 Kč", min: 80000, max: 149999, icon: "💎" },
  { key: "gt150k", label: "Nad 150 000 Kč", min: 150000, max: null, icon: "✨" },
] as const satisfies readonly TripBucket[];

export type PriceBucketKey = (typeof PRICE_BUCKETS)[number]["key"];

/** Kč price slider on /zajezdy. Edge positions emit NO priceMin/priceMax param
 * (open-ended) — trips above the max must not be excluded by the top edge. */
export const PRICE_SLIDER = { min: 8000, max: 300000, step: 1000 } as const;

/** True when `value` falls within `[bucket.min, bucket.max]` (max null = ∞). */
export function inBucket(bucket: TripBucket, value: number): boolean {
  if (value < bucket.min) return false;
  if (bucket.max !== null && value > bucket.max) return false;
  return true;
}

/** Map an array of buckets by key for O(1) lookup. The return type is partial
 * so consumers handle the "unknown key" case explicitly — a raw query-string
 * value like `?duration=typo` would otherwise type-check as a `TripBucket` and
 * NPE on `.min` at runtime. */
export function bucketByKey<B extends TripBucket>(
  buckets: readonly B[],
): Partial<Record<string, B>> {
  return Object.fromEntries(buckets.map((b) => [b.key, b]));
}

export const DURATION_BUCKET_BY_KEY = bucketByKey(DURATION_BUCKETS);
export const PRICE_BUCKET_BY_KEY = bucketByKey(PRICE_BUCKETS);

/** Tuple of bucket keys, with literal narrowing, for `z.enum`. */
export const DURATION_BUCKET_KEYS = DURATION_BUCKETS.map((b) => b.key) as unknown as readonly [
  DurationBucketKey,
  ...DurationBucketKey[],
];
export const PRICE_BUCKET_KEYS = PRICE_BUCKETS.map((b) => b.key) as unknown as readonly [
  PriceBucketKey,
  ...PriceBucketKey[],
];
